import Button from 'ext-ui/components/Button/Button'
import TooltipIcon from 'ext-ui/components/TooltipIcon/TooltipIcon'
import AttachmentFileIcon from 'ext-ui/svg/icon/AttachmentFileIcon'
import UploadCloudIcon from 'ext-ui/svg/icon/UploadCloudIcon'
import React, {
  ReactElement,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { FileRejection, useDropzone } from 'react-dropzone'
import { useTranslate } from 'src/localization/Trns'

import { kybDocumentStyle } from './kybModal.style'
import { KybDocumentValues } from './paymentsKyb.validation'

export interface KybDocumentFieldProps {
  label: string
  microcopy?: string
  /** Optional tooltip shown next to the label (matches the prototype's
   * DocumentUploader tooltip slot). */
  tooltip?: string
  /** Whether this upload is required. When `required`, the label carries the
   * same asterisk indicator as ext-ui `TextInput` so the user knows it gates
   * the step (the step's Next is disabled until it is provided). */
  requirement?: 'required' | 'optional'
  /** An already-translated error message to surface under the field (e.g. a
   * server 422 for the document). */
  errorText?: string
  value: KybDocumentValues | null
  /** The collected file is TRANSIT-ONLY — it lives in the form's local state,
   * rides the single KYB POST to the provider, and is never persisted or shown
   * again. */
  onChange: (doc: KybDocumentValues | null) => void
  testId?: string
}

// The EIN letter / proof-of-address is a PDF or a common image. Enforced before
// we read the file so a huge or arbitrary blob never gets base64-expanded into
// memory or reaches the API.
const MAX_DOCUMENT_BYTES = 10 * 1024 * 1024 // 10MB
const ALLOWED_DOCUMENT_EXT = /\.(pdf|png|jpe?g|webp|heic|heif)$/i
const ALLOWED_DOCUMENT_MIME = new Set([
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/heic',
  'image/heif',
])
// react-dropzone accept map (filters the native picker + drag-drop). Mirrors the
// same set `isAllowedDocument` enforces as the source of truth below.
const DROPZONE_ACCEPT = {
  'application/pdf': ['.pdf'],
  'image/png': ['.png'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/webp': ['.webp'],
  'image/heic': ['.heic'],
  'image/heif': ['.heif'],
}

const isAllowedDocument = (file: File): boolean => {
  const extOk = ALLOWED_DOCUMENT_EXT.test(file.name)
  // Some browsers leave `type` empty for a valid file — fall back to the ext.
  const mimeOk = !file.type || ALLOWED_DOCUMENT_MIME.has(file.type)
  return extOk && mimeOk
}

/**
 * The EIN-letter / proof-of-address uploader. A dashed dropzone (click-to-browse
 * or drag-and-drop) built on `react-dropzone` — the same mechanic the app's
 * receipt/attachment upload (`AddAttachment`) uses — styled with ext-ui tokens
 * to match the invoice-pay prototype's `DocumentUploader`. Reads the chosen file
 * into an inline base64 string (MT `documents[].file_data`) held only in local
 * form state, never persisted (transit-only PII contract).
 */
export function KybDocumentField({
  label,
  microcopy,
  tooltip,
  requirement,
  errorText,
  value,
  onChange,
  testId,
}: KybDocumentFieldProps): ReactElement {
  const translate = useTranslate()
  const errorId = `${testId ?? 'kyb-document'}-error`

  // Track the active read so we can abort it (new selection / unmount) and
  // ignore a stale read that resolves after it was superseded.
  const readerRef = useRef<FileReader | null>(null)
  const [localError, setLocalError] = useState<string | null>(null)

  useEffect(
    () => () => {
      // Abort any in-flight read on unmount (e.g. the modal closed) so a late
      // onload can't repopulate hidden form state after the field is gone.
      readerRef.current?.abort()
      readerRef.current = null
    },
    [],
  )

  const processFile = useCallback(
    (file: File | undefined): void => {
      // Abort/replace any previous in-flight read before starting a new one.
      readerRef.current?.abort()
      readerRef.current = null
      setLocalError(null)

      if (!file) {
        onChange(null)
        return
      }
      if (!isAllowedDocument(file)) {
        onChange(null)
        setLocalError(
          translate('Choose a PDF or image file (PNG, JPG, WEBP, or HEIC).'),
        )
        return
      }
      if (file.size === 0) {
        onChange(null)
        setLocalError(translate('File is empty. Choose a file with content.'))
        return
      }
      if (file.size > MAX_DOCUMENT_BYTES) {
        onChange(null)
        setLocalError(translate('File is too large. Choose a file under 10MB.'))
        return
      }

      const reader = new FileReader()
      readerRef.current = reader
      reader.onload = (): void => {
        if (readerRef.current !== reader) return // superseded/aborted
        readerRef.current = null
        const result = typeof reader.result === 'string' ? reader.result : ''
        const base64 = result.includes(',') ? result.split(',')[1] : result
        onChange({
          documentType: 'ein_letter',
          filename: file.name,
          fileDataBase64: base64,
        })
      }
      reader.onerror = (): void => {
        if (readerRef.current !== reader) return
        readerRef.current = null
        onChange(null)
        setLocalError(translate('We could not read that file. Try another.'))
      }
      reader.onabort = (): void => {
        if (readerRef.current === reader) readerRef.current = null
      }
      reader.readAsDataURL(file)
    },
    [onChange, translate],
  )

  const onDrop = useCallback(
    (accepted: File[], rejections: FileRejection[]): void => {
      if (rejections.length > 0) {
        readerRef.current?.abort()
        readerRef.current = null
        onChange(null)
        setLocalError(
          translate(
            'Choose a PDF or image file (PNG, JPG, WEBP, or HEIC) under 10MB.',
          ),
        )
        return
      }
      processFile(accepted[0])
    },
    [onChange, processFile, translate],
  )

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: DROPZONE_ACCEPT,
    maxSize: MAX_DOCUMENT_BYTES,
    multiple: false,
  })

  const displayError = errorText ?? localError

  return (
    <div className="KybDocument" css={kybDocumentStyle} data-testid={testId}>
      <div className="KybDocument-label">
        <span>{label}</span>
        {requirement === 'required' && (
          <span className="KybDocument-required" aria-hidden="true">
            {'*'}
          </span>
        )}
        {requirement === 'optional' && (
          <span className="KybDocument-optional">
            {translate('(Optional)')}
          </span>
        )}
        {tooltip && <TooltipIcon>{tooltip}</TooltipIcon>}
      </div>

      {value ? (
        <div className="KybDocument-selected">
          <span className="KybDocument-selectedInfo">
            <AttachmentFileIcon width={20} height={20} />
            <span className="KybDocument-fileName">{value.filename}</span>
          </span>
          <Button
            variant="no-style"
            onClick={(): void => {
              setLocalError(null)
              onChange(null)
            }}
            testId={`${testId ?? 'kyb-document'}-remove`}
          >
            {translate('Remove')}
          </Button>
        </div>
      ) : (
        <div
          className={
            isDragActive
              ? 'KybDocument-dropzone dragActive'
              : 'KybDocument-dropzone'
          }
          data-testid={`${testId ?? 'kyb-document'}-dropzone`}
          {...getRootProps()}
        >
          <input
            aria-label={label}
            aria-required={requirement === 'required' || undefined}
            aria-invalid={!!displayError || undefined}
            aria-describedby={displayError ? errorId : undefined}
            data-testid={`${testId ?? 'kyb-document'}-file`}
            {...getInputProps()}
          />
          <UploadCloudIcon
            width={24}
            height={24}
            className="KybDocument-dropzoneIcon"
          />
          <p className="KybDocument-dropzoneText">
            <span className="KybDocument-dropzoneLink">
              {translate('Click to upload')}
            </span>{' '}
            {translate('or drag and drop')}
          </p>
          <p className="KybDocument-dropzoneHint">
            {translate('PDF, PNG, JPG, WEBP, or HEIC (max. 10MB)')}
          </p>
        </div>
      )}

      {displayError && (
        <p id={errorId} role="alert" className="KybDocument-error">
          {displayError}
        </p>
      )}
      {microcopy && !value && !displayError && (
        <p className="KybDocument-microcopy">{microcopy}</p>
      )}
    </div>
  )
}
